import React, { useEffect, useMemo, useState } from "react";
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Divider,
  Grid,
  Group,
  NumberInput,
  Select,
  SegmentedControl,
  Stack,
  Text,
  Textarea,
  TextInput,
  Tooltip,
} from "@mantine/core";
import {
  IconAlertTriangle,
  IconPlus,
  IconTrash,
  IconX,
  IconArrowsSplit,
} from "@tabler/icons-react";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { getApiEndpoint } from "../../config/config";

/** Money is compared in whole cents so a 3-way split never fails on 0.001. */
const toCents = n => Math.round((Number(n) || 0) * 100);
const money = n =>
  `$${(Number(n) || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const personLabel = p =>
  `${p.first_name || ""} ${p.last_name || ""}`.trim() || p.email || `#${p.id}`;

const todayISO = () => new Date().toISOString().slice(0, 10);

const emptyBearer = () => ({
  key: Math.random().toString(36).slice(2),
  party: "USER",
  userId: null,
  basis: "FIXED",
  value: null,
});

const emptyAttribution = () => ({
  key: Math.random().toString(36).slice(2),
  userId: null,
  amount: null,
  note: "",
});

/**
 * Logs one expense with its two independent splits.
 *
 * Renders inline above the ledger rather than in a dialog: the form carries
 * three sections of repeatable rows, which is more than a modal should hold,
 * and keeping it in the page lets the list stay visible while you fill it in.
 *
 * The three party questions stay visually separate because they are separate
 * answers: who *paid*, whose money it ultimately *comes out of* (must total the
 * expense), and who it was *spent on* (may cover only part of it).
 */
function LogExpenseForm({ opened, onClose, onCreated }) {
  const { getVerifiedJWT } = useAuth();
  const { showError, showSuccess } = useToast();

  const [categories, setCategories] = useState([]);
  const [people, setPeople] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const [categoryId, setCategoryId] = useState(null);
  const [amount, setAmount] = useState(null);
  const [vendor, setVendor] = useState("");
  const [description, setDescription] = useState("");
  const [incurredOn, setIncurredOn] = useState(todayISO());
  const [timing, setTiming] = useState("IMMEDIATE");
  const [dueOn, setDueOn] = useState("");
  const [payerParty, setPayerParty] = useState("COMPANY");
  const [payerUserId, setPayerUserId] = useState(null);
  const [bearers, setBearers] = useState([]);
  const [attributions, setAttributions] = useState([]);

  const authedFetch = async (path, init = {}) => {
    const jwt = await getVerifiedJWT();
    return fetch(getApiEndpoint(path), {
      ...init,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${jwt}`,
        ...(init.headers || {}),
      },
    });
  };

  useEffect(() => {
    if (!opened) return;
    let cancelled = false;

    (async () => {
      try {
        const [catRes, peopleRes] = await Promise.all([
          authedFetch("/v1/spend/categories"),
          authedFetch("/v1/spend/people"),
        ]);
        if (cancelled) return;
        if (catRes.ok) {
          const body = await catRes.json();
          setCategories(body.data || []);
        }
        if (peopleRes.ok) {
          const body = await peopleRes.json();
          setPeople(body.data || []);
        }
      } catch {
        if (!cancelled) showError("Could not load categories and people.");
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  const reset = () => {
    setCategoryId(null);
    setAmount(null);
    setVendor("");
    setDescription("");
    setIncurredOn(todayISO());
    setTiming("IMMEDIATE");
    setDueOn("");
    setPayerParty("COMPANY");
    setPayerUserId(null);
    setBearers([]);
    setAttributions([]);
  };

  const close = () => {
    reset();
    onClose();
  };

  const peopleOptions = useMemo(
    () => people.map(p => ({ value: String(p.id), label: personLabel(p) })),
    [people]
  );

  const categoryOptions = useMemo(
    () => categories.map(c => ({ value: String(c.id), label: c.name })),
    [categories]
  );

  // ---- split maths, mirrored from the server so the form can warn early ----

  const totalCents = toCents(amount);

  const bearerCents = bearers.reduce((sum, b) => {
    if (b.basis === "SHARE") {
      return sum + Math.round((totalCents * (Number(b.value) || 0)) / 100);
    }
    return sum + toCents(b.value);
  }, 0);

  const attributedCents = attributions.reduce(
    (sum, a) => sum + toCents(a.amount),
    0
  );

  const bearerRemainder = totalCents - bearerCents;
  const hasShareRow = bearers.some(b => b.basis === "SHARE");
  // Leftover cents land on a SHARE row automatically; an all-FIXED split has to
  // be exact, because adjusting it would contradict what was typed.
  const bearerBalanced =
    bearers.length === 0 || bearerRemainder === 0 || hasShareRow;
  const overAttributed = attributedCents > totalCents;

  const splitEvenly = () => {
    const targets = bearers.filter(b => b.party === "COMPANY" || b.userId);
    if (targets.length === 0) return;
    const share = Math.floor((100 / targets.length) * 100) / 100;
    setBearers(prev =>
      prev.map((b, i) => ({
        ...b,
        basis: "SHARE",
        value:
          i === 0
            ? Number((100 - share * (prev.length - 1)).toFixed(2))
            : share,
      }))
    );
  };

  const bearerAmountPreview = b => {
    if (b.basis === "SHARE") {
      return Math.round((totalCents * (Number(b.value) || 0)) / 100) / 100;
    }
    return Number(b.value) || 0;
  };

  const canSubmit =
    categoryId &&
    totalCents > 0 &&
    incurredOn &&
    (timing === "IMMEDIATE" || dueOn) &&
    (payerParty === "COMPANY" || payerUserId) &&
    bearerBalanced &&
    !overAttributed &&
    !submitting;

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        budget_category_id: Number(categoryId),
        amount: Number(amount),
        incurred_on: incurredOn,
        payment_timing: timing,
        paid_by_party: payerParty,
        ...(vendor.trim() ? { vendor: vendor.trim() } : {}),
        ...(description.trim() ? { description: description.trim() } : {}),
        ...(timing === "DEFERRED" ? { due_on: dueOn } : {}),
        ...(payerParty === "USER"
          ? { paid_by_user_id: Number(payerUserId) }
          : {}),
      };

      const usable = bearers.filter(
        b => (b.party === "COMPANY" || b.userId) && b.value !== null
      );
      if (usable.length > 0) {
        payload.bearers = usable.map(b => ({
          bearer_party: b.party,
          ...(b.party === "USER" ? { bearer_user_id: Number(b.userId) } : {}),
          split_basis: b.basis,
          ...(b.basis === "SHARE"
            ? { share_percent: Number(b.value) }
            : { amount: Number(b.value) }),
        }));
      }

      const usableAttrs = attributions.filter(a => a.userId && a.amount);
      if (usableAttrs.length > 0) {
        payload.attributions = usableAttrs.map(a => ({
          user_id: Number(a.userId),
          amount: Number(a.amount),
          ...(a.note.trim() ? { note: a.note.trim() } : {}),
        }));
      }

      const res = await authedFetch("/v1/spend/expenses", {
        method: "POST",
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        // The API returns a bare string for handled rejections and an object
        // for validation failures; both carry the message worth showing.
        const raw = await res.text();
        let message = raw;
        try {
          const parsed = JSON.parse(raw);
          message =
            typeof parsed === "string"
              ? parsed
              : parsed.message || "Could not log this expense.";
        } catch {
          message = raw.replace(/^"|"$/g, "");
        }
        showError(Array.isArray(message) ? message.join(", ") : message);
        return;
      }

      showSuccess("Expense logged.");
      reset();
      onCreated?.();
      onClose();
    } catch {
      showError("Could not log this expense.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!opened) return null;

  return (
    <section className="bt-panel" aria-label="Log an expense">
      <header className="bt-panel-head">
        <Text fw={700} size="lg">
          Log an expense
        </Text>
        <ActionIcon
          variant="subtle"
          color="gray"
          onClick={close}
          aria-label="Close"
        >
          <IconX size={18} />
        </ActionIcon>
      </header>

      <div className="bt-panel-body">
        <Stack gap="lg">
          {/* ---- what was bought ---- */}
          <Grid gutter="sm">
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <Select
                label="Category"
                placeholder="Pick a category"
                data={categoryOptions}
                value={categoryId}
                onChange={setCategoryId}
                searchable
                required
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <NumberInput
                label="Amount"
                placeholder="0.00"
                prefix="$"
                decimalScale={2}
                min={0}
                value={amount}
                onChange={setAmount}
                required
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <TextInput
                label="Vendor"
                placeholder="Who got paid"
                value={vendor}
                onChange={e => setVendor(e.currentTarget.value)}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <TextInput
                label="Date incurred"
                type="date"
                value={incurredOn}
                onChange={e => setIncurredOn(e.currentTarget.value)}
                required
              />
            </Grid.Col>
            <Grid.Col span={12}>
              <Textarea
                label="Description"
                placeholder="What was this for?"
                autosize
                minRows={1}
                value={description}
                onChange={e => setDescription(e.currentTarget.value)}
              />
            </Grid.Col>
          </Grid>

          <Divider />

          {/* ---- who paid, and when ---- */}
          <Stack gap="sm">
            <Text fw={600} size="sm">
              Who paid
            </Text>
            <Group align="flex-end" gap="sm" wrap="wrap">
              <SegmentedControl
                value={payerParty}
                onChange={v => {
                  setPayerParty(v);
                  if (v === "COMPANY") setPayerUserId(null);
                }}
                data={[
                  { label: "Company card", value: "COMPANY" },
                  { label: "A person", value: "USER" },
                ]}
              />
              {payerParty === "USER" && (
                <Select
                  placeholder="Who fronted it"
                  data={peopleOptions}
                  value={payerUserId}
                  onChange={setPayerUserId}
                  searchable
                  style={{ minWidth: 220 }}
                />
              )}
              <SegmentedControl
                value={timing}
                onChange={v => {
                  setTiming(v);
                  if (v === "IMMEDIATE") setDueOn("");
                }}
                data={[
                  { label: "Paid now", value: "IMMEDIATE" },
                  { label: "Paid later", value: "DEFERRED" },
                ]}
              />
              {timing === "DEFERRED" && (
                <TextInput
                  label="Due"
                  type="date"
                  value={dueOn}
                  onChange={e => setDueOn(e.currentTarget.value)}
                  required
                />
              )}
            </Group>
          </Stack>

          <Divider />

          {/* ---- whose money it comes out of ---- */}
          <Stack gap="sm">
            <Group justify="space-between" align="center">
              <div>
                <Text fw={600} size="sm">
                  Who it comes out of
                </Text>
                <Text size="xs" c="dimmed">
                  Must add up to the full amount. Leave empty to use the
                  category&rsquo;s default.
                </Text>
              </div>
              <Group gap="xs">
                {bearers.length > 1 && (
                  <Tooltip label="Give everyone an equal share">
                    <Button
                      size="xs"
                      variant="light"
                      leftSection={<IconArrowsSplit size={14} />}
                      onClick={splitEvenly}
                    >
                      Split evenly
                    </Button>
                  </Tooltip>
                )}
                <Button
                  size="xs"
                  variant="light"
                  leftSection={<IconPlus size={14} />}
                  onClick={() => setBearers(p => [...p, emptyBearer()])}
                >
                  Add person
                </Button>
              </Group>
            </Group>

            {bearers.map((b, idx) => (
              <Group key={b.key} gap="xs" align="flex-end" wrap="nowrap">
                <SegmentedControl
                  size="xs"
                  value={b.party}
                  onChange={v =>
                    setBearers(p =>
                      p.map((row, i) =>
                        i === idx
                          ? {
                              ...row,
                              party: v,
                              userId: v === "COMPANY" ? null : row.userId,
                            }
                          : row
                      )
                    )
                  }
                  data={[
                    { label: "Person", value: "USER" },
                    { label: "Company", value: "COMPANY" },
                  ]}
                />
                <Select
                  placeholder={
                    b.party === "COMPANY" ? "The company" : "Pick a person"
                  }
                  data={peopleOptions}
                  value={b.userId}
                  disabled={b.party === "COMPANY"}
                  onChange={v =>
                    setBearers(p =>
                      p.map((row, i) =>
                        i === idx ? { ...row, userId: v } : row
                      )
                    )
                  }
                  searchable
                  style={{ flex: 1, minWidth: 160 }}
                />
                <SegmentedControl
                  size="xs"
                  value={b.basis}
                  onChange={v =>
                    setBearers(p =>
                      p.map((row, i) =>
                        i === idx ? { ...row, basis: v } : row
                      )
                    )
                  }
                  data={[
                    { label: "%", value: "SHARE" },
                    { label: "$", value: "FIXED" },
                  ]}
                />
                <NumberInput
                  placeholder={b.basis === "SHARE" ? "25" : "0.00"}
                  decimalScale={2}
                  min={0}
                  max={b.basis === "SHARE" ? 100 : undefined}
                  value={b.value}
                  onChange={v =>
                    setBearers(p =>
                      p.map((row, i) =>
                        i === idx ? { ...row, value: v } : row
                      )
                    )
                  }
                  style={{ width: 110 }}
                />
                <Text
                  size="sm"
                  c="dimmed"
                  style={{ width: 90, textAlign: "right" }}
                >
                  {money(bearerAmountPreview(b))}
                </Text>
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  onClick={() => setBearers(p => p.filter((_, i) => i !== idx))}
                  aria-label="Remove"
                >
                  <IconTrash size={16} />
                </ActionIcon>
              </Group>
            ))}

            {bearers.length > 0 && (
              <Group justify="flex-end" gap="xs">
                <Text size="sm" c="dimmed">
                  Split total {money(bearerCents / 100)} of{" "}
                  {money(totalCents / 100)}
                </Text>
                {bearerRemainder === 0 ? (
                  <Badge color="green" variant="light">
                    Balanced
                  </Badge>
                ) : hasShareRow ? (
                  <Badge color="blue" variant="light">
                    {money(Math.abs(bearerRemainder) / 100)} goes to the first %
                    share
                  </Badge>
                ) : (
                  <Badge color="red" variant="light">
                    {money(Math.abs(bearerRemainder) / 100)}{" "}
                    {bearerRemainder > 0 ? "unassigned" : "over"}
                  </Badge>
                )}
              </Group>
            )}
          </Stack>

          <Divider />

          {/* ---- who it was spent on ---- */}
          <Stack gap="sm">
            <Group justify="space-between" align="center">
              <div>
                <Text fw={600} size="sm">
                  Who it was spent on
                </Text>
                <Text size="xs" c="dimmed">
                  Optional, and moves no money. Anything left over counts as
                  overhead.
                </Text>
              </div>
              <Button
                size="xs"
                variant="light"
                leftSection={<IconPlus size={14} />}
                onClick={() => setAttributions(p => [...p, emptyAttribution()])}
              >
                Add a rep
              </Button>
            </Group>

            {attributions.map((a, idx) => (
              <Group key={a.key} gap="xs" align="flex-end" wrap="nowrap">
                <Select
                  placeholder="Pick a rep"
                  data={peopleOptions}
                  value={a.userId}
                  onChange={v =>
                    setAttributions(p =>
                      p.map((row, i) =>
                        i === idx ? { ...row, userId: v } : row
                      )
                    )
                  }
                  searchable
                  style={{ flex: 1, minWidth: 160 }}
                />
                <NumberInput
                  placeholder="0.00"
                  prefix="$"
                  decimalScale={2}
                  min={0}
                  value={a.amount}
                  onChange={v =>
                    setAttributions(p =>
                      p.map((row, i) =>
                        i === idx ? { ...row, amount: v } : row
                      )
                    )
                  }
                  style={{ width: 140 }}
                />
                <TextInput
                  placeholder="Note (optional)"
                  value={a.note}
                  onChange={e => {
                    const value = e.currentTarget.value;
                    setAttributions(p =>
                      p.map((row, i) =>
                        i === idx ? { ...row, note: value } : row
                      )
                    );
                  }}
                  style={{ flex: 1, minWidth: 140 }}
                />
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  onClick={() =>
                    setAttributions(p => p.filter((_, i) => i !== idx))
                  }
                  aria-label="Remove"
                >
                  <IconTrash size={16} />
                </ActionIcon>
              </Group>
            ))}

            {overAttributed && (
              <Alert
                color="red"
                variant="light"
                icon={<IconAlertTriangle size={16} />}
              >
                Attributions add up to {money(attributedCents / 100)}, more than
                the {money(totalCents / 100)} expense.
              </Alert>
            )}
          </Stack>

          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={close}>
              Cancel
            </Button>
            <Button onClick={submit} loading={submitting} disabled={!canSubmit}>
              Log expense
            </Button>
          </Group>
        </Stack>
      </div>
    </section>
  );
}

export default LogExpenseForm;
